/**
 * Utility to dynamically determine the admin URL based on the current environment
 */

/**
 * Check if we're currently in production environment
 * @returns true if in production, false otherwise
 */
function isProductionEnv(): boolean {
  return (
    import.meta.env.VITE_IS_IN_PRODUCTION === 'true' ||
    import.meta.env.PROD === true ||
    window.location.hostname === 'clarke.com.br' ||
    window.location.hostname === 'sales.clarke.com.br'
  );
}

/**
 * Get the admin base URL based on the current environment
 * @returns The admin base URL (https://admin.clarke.com.br for production, https://beta-admin.clarke.com.br for beta/dev)
 */
export function getAdminBaseUrl(): string {
  return isProductionEnv() ? 'https://admin.clarke.com.br' : 'https://beta-admin.clarke.com.br';
}

/**
 * Get the admin remote entry URL for module federation
 * @returns The full remote entry URL
 */
export function getAdminRemoteEntryUrl(): string {
  return `${getAdminBaseUrl()}/remoteEntry.js`;
}

/**
 * Get the admin URL for a specific path
 * @param path - The path to append to the admin base URL
 * @returns The full admin URL with the specified path
 */
export function getAdminUrl(path = ''): string {
  const baseUrl = getAdminBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Check if we're currently in production environment
 * @returns true if in production, false otherwise
 */
export function isProductionEnvironment(): boolean {
  return isProductionEnv();
}

/**
 * Check if we're currently in local development environment
 * @returns true if in local development, false otherwise
 */
export function isLocalDevelopment(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.startsWith('10.') ||
    window.location.hostname.endsWith('.local')
  );
}
